/**
 * Phase 2 · Simplified Folder Presets (Msg 588).
 *
 * Consolidated admin scaffolding UI. Replaces the legacy trio
 * (Templates, Parent map, Bulk attributes). Admins select one or
 * more target folders (individual or "folder + all descendants"),
 * list sub-folders to add, list attributes to add (Name + Answer
 * Type), preview, and apply idempotently.
 *
 * Backend: POST /api/atlas/admin/presets/apply
 *
 * Guardrails:
 *   - No changes to left rail, focus pages, icons, or move-to-top.
 *   - Legacy admin routes redirect here with a `legacy` banner.
 *   - Backend `entity_type` field name stays; admin-facing UI copy
 *     says "Folder Type".
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Plus, Trash2, X as CloseIcon, ChevronRight, ChevronDown, Search,
  Loader2, Check, AlertCircle, Layers, Sparkles, ArrowLeft,
  Info, FolderPlus, Tag as TagIcon, Save, Zap,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EntityIcon, humanize } from "../AtlasTopicShared";
import { useAtlasCategories } from "../atlasCategoriesCache";
import { useAtlasAttributes } from "../atlasAttributesCache";
import { iconByName } from "../topicIconMap";


// ─────────────────────────────────────────────────────────────
// Answer-type dropdown values. Front-end labels → backend keys.
// ─────────────────────────────────────────────────────────────
const ANSWER_TYPES = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date",   label: "Date" },
  { value: "bool",   label: "Yes / No" },
  { value: "list",   label: "Pick from list" },
];


// ─────────────────────────────────────────────────────────────
// Legacy-redirect info banner. Rendered when ?legacy=<name> is set.
// ─────────────────────────────────────────────────────────────
function LegacyBanner({ legacyName, onDismiss }) {
  if (!legacyName) return null;
  const labels = {
    templates: "Templates",
    "parent-map": "Parent map",
    bulk: "Bulk attributes",
  };
  const label = labels[legacyName] || legacyName;
  return (
    <div
      data-testid="atlas-presets-legacy-banner"
      className="mb-6 flex items-start gap-3 rounded-2xl border border-tb-navy/15 bg-tb-navy/[0.03] p-4 text-tb-navy"
    >
      <Info className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm font-hand">
        This screen has been merged into <b>Folder Presets</b>.
        The old <b>{label}</b> tool is retired; the same scaffolding
        can now be applied to any folder (with an optional descendant
        fan-out) from here.
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        data-testid="atlas-presets-legacy-banner-dismiss"
        className="text-tb-navy/50 hover:text-tb-navy"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Target folder picker. Two modes:
//   1. By Folder — search + expandable tree (lazy-loading children
//      at ANY depth) with per-row descendants toggle.
//   2. By Category — list of user-defined categories with checkboxes,
//      showing folder-count per category.
// Selected items across both modes flow into the same `selected`
// array; each entry carries a `type` discriminator ("folder" or
// "category") that the backend consumes.
// ─────────────────────────────────────────────────────────────
function TargetPicker({ selected, onChange }) {
  const [tab, setTab] = useState("folder"); // "folder" | "category"
  const categories = useAtlasCategories();

  const [rootRows, setRootRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [childCache, setChildCache] = useState({});
  const [loadingRoots, setLoadingRoots] = useState(false);
  const [loadingSlugs, setLoadingSlugs] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [revealTarget, setRevealTarget] = useState(null);

  useEffect(() => {
    if (tab !== "folder") return;
    setLoadingRoots(true);
    api.get("/atlas/topics/tree?depth=1")
      .then((r) => setRootRows(r.data?.tree || []))
      .catch(() => setRootRows([]))
      .finally(() => setLoadingRoots(false));
  }, [tab]);

  useEffect(() => {
    const qn = q.trim();
    if (qn.length < 2) { setSearchResults([]); setSearching(false); return; }
    let cancel = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/atlas/topics/search?q=${encodeURIComponent(qn)}&limit=25`)
        .then((r) => { if (!cancel) setSearchResults(r.data?.results || r.data?.topics || []); })
        .catch(() => { if (!cancel) setSearchResults([]); })
        .finally(() => { if (!cancel) setSearching(false); });
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  const loadChildren = useCallback(async (slug) => {
    setLoadingSlugs((prev) => { const n = new Set(prev); n.add(slug); return n; });
    try {
      const r = await api.get(`/atlas/topics/${slug}/children`);
      setChildCache((prev) => ({ ...prev, [slug]: r.data?.children || [] }));
      return r.data?.children || [];
    } catch {
      setChildCache((prev) => ({ ...prev, [slug]: [] }));
      return [];
    } finally {
      setLoadingSlugs((prev) => { const n = new Set(prev); n.delete(slug); return n; });
    }
  }, []);

  const toggleOpen = (slug) => {
    setOpenSet((prev) => {
      const n = new Set(prev);
      if (n.has(slug)) n.delete(slug);
      else {
        n.add(slug);
        if (!childCache[slug]) loadChildren(slug);
      }
      return n;
    });
  };

  const isSelected = (slug) => selected.some((s) => s.type === "folder" && s.slug === slug);
  const findSelected = (slug) => selected.find((s) => s.type === "folder" && s.slug === slug);

  const toggleSelect = (row) => {
    if (isSelected(row.slug)) {
      onChange(selected.filter((s) => !(s.type === "folder" && s.slug === row.slug)));
    } else {
      onChange([...selected, {
        type: "folder",
        slug: row.slug,
        name: row.name,
        entity_type: row.entity_type,
        include_descendants: false,
      }]);
    }
  };

  const toggleDescendants = (slug) => {
    onChange(selected.map((s) =>
      s.type === "folder" && s.slug === slug
        ? { ...s, include_descendants: !s.include_descendants }
        : s));
  };

  // ── Category-tab helpers ──
  const isCategorySelected = (cid) => selected.some(
    (s) => s.type === "category" && s.category_id === cid);
  const toggleCategorySelect = (cat) => {
    if (isCategorySelected(cat.id)) {
      onChange(selected.filter((s) => !(s.type === "category" && s.category_id === cat.id)));
    } else {
      onChange([...selected, {
        type: "category",
        category_id: cat.id,
        name: cat.name,
        icon_name: cat.icon_name,
        folder_count: cat.folder_count,
      }]);
    }
  };

  // Reveal a slug in the tree: expand every ancestor + prime the
  // child cache along the way, then clear search so the tree view
  // renders and scroll to the row.
  const revealInTree = useCallback(async (result) => {
    const chain = (result.ancestor_path || []).map((a) => a.slug);
    // Sequentially fetch + open each level so the tree renders down
    // to the target row.
    const nextOpen = new Set(openSet);
    for (const slug of chain) {
      nextOpen.add(slug);
      if (!childCache[slug]) {
        // eslint-disable-next-line no-await-in-loop
        await loadChildren(slug);
      }
    }
    // Also open the target's own subtree if it has children.
    if ((result.child_count || 0) > 0) {
      nextOpen.add(result.slug);
      if (!childCache[result.slug]) {
        await loadChildren(result.slug);
      }
    }
    setOpenSet(nextOpen);
    setRevealTarget(result.slug);
    setQ(""); // clear search so tree renders
  }, [openSet, childCache, loadChildren]);

  // Scroll the reveal target into view after tree rehydrates.
  useEffect(() => {
    if (!revealTarget) return;
    const t = setTimeout(() => {
      try {
        const el = document.querySelector(
          `[data-testid="atlas-presets-target-row-${revealTarget}"]`
        );
        if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch { /* noop */ }
      setRevealTarget(null);
    }, 250);
    return () => clearTimeout(t);
  }, [revealTarget, childCache]);

  const renderRow = (row, depth) => {
    const kids = childCache[row.slug];
    const isOpen = openSet.has(row.slug);
    // Show chevron when the row is known to have children. Fall back
    // to `descendant_count` (populated on `/tree` responses) so root
    // rows also chevron correctly. When we've fetched and got an
    // empty list, hide the chevron (`kids && kids.length === 0`).
    const knownLeaf = Array.isArray(kids) && kids.length === 0;
    const canExpand = !knownLeaf && (
      (typeof row.child_count === "number" && row.child_count > 0)
      || (typeof row.descendant_count === "number" && row.descendant_count > 0)
      || (Array.isArray(kids) && kids.length > 0)
    );
    const sel = isSelected(row.slug);
    const meta = findSelected(row.slug);
    const highlight = revealTarget === row.slug;
    return (
      <React.Fragment key={row.slug}>
        <div
          data-testid={`atlas-presets-target-row-${row.slug}`}
          className={"flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-tb-navy/5 "
            + (highlight ? "ring-2 ring-tb-gold-bright " : "")
            + (sel ? "bg-tb-navy/[0.04]" : "")}
          style={{ paddingLeft: 8 + depth * 18 }}
        >
          {canExpand ? (
            <button type="button" onClick={() => toggleOpen(row.slug)}
              aria-label={isOpen ? "Collapse" : "Expand"}
              data-testid={`atlas-presets-target-expand-${row.slug}`}
              className="text-tb-navy/50 hover:text-tb-navy shrink-0">
              {loadingSlugs.has(row.slug)
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : isOpen ? <ChevronDown className="w-3.5 h-3.5" />
                          : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : <span className="w-3.5 shrink-0" />}
          <input
            type="checkbox"
            checked={sel}
            onChange={() => toggleSelect(row)}
            data-testid={`atlas-presets-target-check-${row.slug}`}
            className="w-4 h-4 accent-tb-navy cursor-pointer"
          />
          <EntityIcon categoryId={row.category_id} className="w-3.5 h-3.5 text-tb-navy/60 shrink-0" />
          <span className="text-sm text-tb-navy truncate">{row.name}</span>
          {typeof row.child_count === "number" && row.child_count > 0 && (
            <span className="text-[10px] font-hand text-tb-navy/50 shrink-0">
              {row.child_count}
            </span>
          )}
          {sel && (
            <label
              className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-hand text-tb-navy/70 cursor-pointer shrink-0"
              data-testid={`atlas-presets-target-descendants-${row.slug}`}
            >
              <input
                type="checkbox"
                checked={!!meta?.include_descendants}
                onChange={() => toggleDescendants(row.slug)}
                className="w-3.5 h-3.5 accent-tb-navy cursor-pointer"
              />
              + descendants
            </label>
          )}
        </div>
        {isOpen && Array.isArray(kids) && kids.map((c) => renderRow(c, depth + 1))}
      </React.Fragment>
    );
  };

  const renderSearchResult = (row) => {
    const sel = isSelected(row.slug);
    const meta = findSelected(row.slug);
    const path = row.ancestor_path || [];
    return (
      <div
        key={row.slug}
        data-testid={`atlas-presets-target-search-row-${row.slug}`}
        className={"flex items-start gap-2 rounded-lg px-2 py-2 hover:bg-tb-navy/5 "
          + (sel ? "bg-tb-navy/[0.04]" : "")}
      >
        <input
          type="checkbox"
          checked={sel}
          onChange={() => toggleSelect(row)}
          data-testid={`atlas-presets-target-check-${row.slug}`}
          className="w-4 h-4 mt-0.5 accent-tb-navy cursor-pointer"
        />
        <EntityIcon categoryId={row.category_id} className="w-3.5 h-3.5 text-tb-navy/60 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {path.length > 0 && (
            <div className="flex flex-wrap items-center gap-0.5 text-[10px] font-hand text-tb-navy/50 mb-0.5"
                 data-testid={`atlas-presets-target-search-breadcrumb-${row.slug}`}>
              {path.map((a, i) => (
                <React.Fragment key={a.slug}>
                  <span>{a.name}</span>
                  {i < path.length - 1 && <ChevronRight className="w-2.5 h-2.5 shrink-0" />}
                </React.Fragment>
              ))}
              <ChevronRight className="w-2.5 h-2.5 shrink-0" />
            </div>
          )}
          <div className="text-sm text-tb-navy truncate">{row.name}</div>
        </div>
        <button
          type="button"
          onClick={() => revealInTree(row)}
          data-testid={`atlas-presets-target-search-reveal-${row.slug}`}
          title="Reveal in tree"
          className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/60 hover:text-tb-navy border border-tb-navy/15 rounded-full px-2 py-0.5 shrink-0"
        >
          Reveal
        </button>
        {sel && (
          <label
            className="inline-flex items-center gap-1 text-[11px] font-hand text-tb-navy/70 cursor-pointer shrink-0"
            data-testid={`atlas-presets-target-descendants-${row.slug}`}
          >
            <input
              type="checkbox"
              checked={!!meta?.include_descendants}
              onChange={() => toggleDescendants(row.slug)}
              className="w-3.5 h-3.5 accent-tb-navy cursor-pointer"
            />
            + descendants
          </label>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-tb-navy/15 bg-white overflow-hidden"
         data-testid="atlas-presets-target-picker">
      {/* Tab bar — Msg 590 · Phase A follow-up (Issue 3) */}
      <div className="flex border-b border-tb-navy/10" data-testid="atlas-presets-target-tabs">
        <button
          type="button"
          onClick={() => setTab("folder")}
          data-testid="atlas-presets-target-tab-folder"
          className={"flex-1 text-xs font-hand uppercase tracking-widest px-4 py-2.5 transition "
            + (tab === "folder"
                ? "bg-tb-navy text-tb-gold-bright"
                : "text-tb-navy/60 hover:bg-tb-navy/5")}
        >
          By Folder
        </button>
        <button
          type="button"
          onClick={() => setTab("category")}
          data-testid="atlas-presets-target-tab-category"
          className={"flex-1 text-xs font-hand uppercase tracking-widest px-4 py-2.5 transition "
            + (tab === "category"
                ? "bg-tb-navy text-tb-gold-bright"
                : "text-tb-navy/60 hover:bg-tb-navy/5")}
        >
          By Category
        </button>
      </div>

      {tab === "folder" ? (
        <>
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-tb-navy/10 px-3 py-2">
            <Search className="w-4 h-4 text-tb-navy/50" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search folders — try 'silversea', 'japan', 'luxury cruise lines'…"
              data-testid="atlas-presets-target-search"
              className="flex-1 bg-transparent outline-none text-sm text-tb-navy placeholder:text-tb-navy/40"
            />
            {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-tb-navy/50" />}
            {q && (
              <button type="button" onClick={() => setQ("")}
                      aria-label="Clear search"
                      data-testid="atlas-presets-target-search-clear"
                      className="text-tb-navy/40 hover:text-tb-navy">
                <CloseIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Search results — flat list with breadcrumb */}
          {q.trim().length >= 2 ? (
            <div className="max-h-[400px] overflow-y-auto p-2 space-y-0.5"
                 data-testid="atlas-presets-target-search-results">
              {!searching && searchResults.length === 0 && (
                <div className="text-center py-6 text-tb-navy/50 text-sm font-hand">
                  No matches.
                </div>
              )}
              {searchResults.map((r) => renderSearchResult(r))}
            </div>
          ) : (
            /* Tree — lazy-loads at any depth */
            <div className="max-h-[400px] overflow-y-auto p-2 space-y-0.5"
                 data-testid="atlas-presets-target-tree">
              {loadingRoots && (
                <div className="flex items-center justify-center py-6 text-tb-navy/50">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
              {!loadingRoots && rootRows.length === 0 && (
                <div className="text-center py-6 text-tb-navy/50 text-sm font-hand">
                  No root folders yet.
                </div>
              )}
              {!loadingRoots && rootRows.map((r) => renderRow(r, 0))}
            </div>
          )}
        </>
      ) : (
        /* Category tab */
        <div className="max-h-[400px] overflow-y-auto p-2 space-y-0.5"
             data-testid="atlas-presets-target-category-list">
          {categories.length === 0 && (
            <div className="text-center py-6 text-tb-navy/50 text-sm font-hand">
              No categories yet. Create some in <b>Admin → Categories</b> first.
            </div>
          )}
          {categories.map((c) => {
            const Icon = iconByName(c.icon_name);
            const sel = isCategorySelected(c.id);
            return (
              <label
                key={c.id}
                data-testid={`atlas-presets-target-category-row-${c.id}`}
                className={"flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer hover:bg-tb-navy/5 "
                  + (sel ? "bg-tb-navy/[0.04]" : "")}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  onChange={() => toggleCategorySelect(c)}
                  data-testid={`atlas-presets-target-category-check-${c.id}`}
                  className="w-4 h-4 accent-tb-navy cursor-pointer"
                />
                <div className="w-7 h-7 rounded-full bg-tb-navy/[0.06] flex items-center justify-center shrink-0">
                  <Icon className="w-3.5 h-3.5 text-tb-navy" />
                </div>
                <span className="text-sm text-tb-navy flex-1 truncate">{c.name}</span>
                <span className="text-[10px] font-hand text-tb-navy/50 shrink-0">
                  {c.folder_count ?? 0} folder{c.folder_count === 1 ? "" : "s"}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* Selected pills bar — mixed folders + categories */}
      {selected.length > 0 && (
        <div className="border-t border-tb-navy/10 bg-tb-navy/[0.02] px-3 py-2"
             data-testid="atlas-presets-target-selected-bar">
          <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-1.5">
            Selected · {selected.length}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((s, i) => {
              const key = s.type === "category" ? `cat:${s.category_id}` : `folder:${s.slug}`;
              return (
                <span
                  key={key}
                  data-testid={s.type === "category"
                    ? `atlas-presets-target-pill-cat-${s.category_id}`
                    : `atlas-presets-target-pill-${s.slug}`}
                  className={"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] "
                    + (s.type === "category"
                        ? "bg-tb-gold-bright text-tb-navy"
                        : "bg-tb-navy text-tb-gold-bright")}
                >
                  {s.type === "category" && (
                    <span className="text-[9px] uppercase tracking-widest opacity-70">Cat:</span>
                  )}
                  {s.name}
                  {s.type === "folder" && s.include_descendants && (
                    <span className="text-[9px] uppercase tracking-widest opacity-80">+ inside</span>
                  )}
                  <button type="button"
                          onClick={() => onChange(selected.filter((_, j) => j !== i))}
                          aria-label={`Remove ${s.name}`}
                          className={s.type === "category" ? "text-tb-navy/70 hover:text-tb-navy" : "text-tb-gold-bright/80 hover:text-tb-gold-bright"}>
                    <CloseIcon className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Sub-folders editor — name-only rows.
// ─────────────────────────────────────────────────────────────
function SubFoldersEditor({ rows, onChange }) {
  const add = () => onChange([...rows, { name: "" }]);
  const patch = (i, name) => onChange(rows.map((r, j) => j === i ? { name } : r));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 space-y-2"
         data-testid="atlas-presets-subfolders">
      {rows.length === 0 && (
        <div className="text-sm text-tb-navy/50 font-hand py-2">
          No sub-folders yet. Add one below (e.g. "Casino", "Dining", "History & Culture").
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2"
             data-testid={`atlas-presets-subfolder-row-${i}`}>
          <FolderPlus className="w-3.5 h-3.5 text-tb-navy/50 shrink-0" />
          <input
            type="text"
            value={r.name}
            onChange={(e) => patch(i, e.target.value)}
            placeholder="Sub-folder name"
            data-testid={`atlas-presets-subfolder-name-${i}`}
            className="flex-1 rounded-lg border border-tb-navy/15 px-3 py-1.5 text-sm text-tb-navy outline-none focus:border-tb-navy"
          />
          <button type="button" onClick={() => remove(i)}
                  aria-label="Remove"
                  data-testid={`atlas-presets-subfolder-remove-${i}`}
                  className="text-tb-navy/50 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="atlas-presets-subfolder-add"
        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-tb-navy/25 px-3 py-1.5 text-xs font-hand text-tb-navy hover:bg-tb-navy/5"
      >
        <Plus className="w-3.5 h-3.5" /> Add sub-folder
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Attributes editor — Name + Answer Type (with options chip
// input when Answer Type is "Pick from list").
// ─────────────────────────────────────────────────────────────
function AttributesEditor({ rows, onChange }) {
  const library = useAtlasAttributes();
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const add = () => onChange([...rows, { name: "", answer_type: "string", options: [] }]);
  const patch = (i, next) => onChange(rows.map((r, j) => j === i ? { ...r, ...next } : r));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  // Msg 604 · Phase B — pick from library. Adds a row referencing
  // atlas_attributes.id so this preset carries the stable library
  // ref through to each target folder's custom_attribute_schema.
  const alreadyAdded = new Set(rows.map((r) => r.attribute_id).filter(Boolean));
  const filteredLibrary = library.filter((a) => {
    if (alreadyAdded.has(a.id)) return false;
    const q = librarySearch.trim().toLowerCase();
    if (!q) return true;
    return (a.name || "").toLowerCase().includes(q)
        || (a.key || "").toLowerCase().includes(q);
  });
  const pickFromLibrary = (a) => {
    onChange([
      ...rows,
      {
        name: a.name,
        answer_type: a.answer_type,
        options: a.answer_type === "list" ? (a.options || []) : [],
        attribute_id: a.id,
      },
    ]);
    setShowLibraryPicker(false);
    setLibrarySearch("");
  };

  return (
    <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 space-y-3"
         data-testid="atlas-presets-attributes">
      {rows.length === 0 && (
        <div className="text-sm text-tb-navy/50 font-hand py-2">
          No attributes yet. Add fields advisors will fill in on each folder
          (e.g. "Star rating", "Has spa", "Casino size").
        </div>
      )}
      {rows.map((r, i) => (
        <AttributeRow
          key={i}
          i={i}
          row={r}
          onPatch={(next) => patch(i, next)}
          onRemove={() => remove(i)}
        />
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={add}
          data-testid="atlas-presets-attribute-add"
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-tb-navy/25 px-3 py-1.5 text-xs font-hand text-tb-navy hover:bg-tb-navy/5"
        >
          <Plus className="w-3.5 h-3.5" /> Add attribute
        </button>
        {/* Msg 604 · Phase B — pick an attribute from the global
            library so N target folders all end up referencing the
            same library id. */}
        <button
          type="button"
          onClick={() => setShowLibraryPicker((v) => !v)}
          data-testid="atlas-presets-attribute-from-library"
          className="inline-flex items-center gap-1.5 rounded-full border border-tb-navy/25 px-3 py-1.5 text-xs font-hand text-tb-navy hover:bg-tb-navy/5"
        >
          <Plus className="w-3.5 h-3.5" /> Add from library
        </button>
      </div>
      {showLibraryPicker && (
        <div
          className="rounded-2xl border border-tb-navy/20 bg-tb-navy/[0.03] p-3 space-y-2"
          data-testid="atlas-presets-library-picker"
        >
          <input
            type="text"
            autoFocus
            placeholder="Search the library…"
            value={librarySearch}
            onChange={(e) => setLibrarySearch(e.target.value)}
            data-testid="atlas-presets-library-search"
            className="w-full h-9 px-3 rounded-lg border border-tb-navy/20 outline-none focus:border-tb-navy text-sm font-hand text-tb-navy"
          />
          <div className="max-h-56 overflow-y-auto rounded-lg divide-y divide-tb-navy/5 bg-white">
            {filteredLibrary.length === 0 && (
              <div className="p-3 text-xs font-hand text-tb-navy/50 text-center">
                {library.length === 0
                  ? "Library is empty. Create some attributes in Admin → Attributes."
                  : "No matching library entries."}
              </div>
            )}
            {filteredLibrary.slice(0, 30).map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => pickFromLibrary(a)}
                data-testid={`atlas-presets-library-pick-${a.id}`}
                className="w-full text-left px-3 py-2 hover:bg-tb-navy/5 flex items-center gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-tb-navy font-semibold truncate">
                    {a.name}
                  </div>
                  <div className="text-[10px] font-mono text-tb-navy/50">
                    {a.key} · {a.answer_type}
                    {a.answer_type === "list" && (a.options || []).length
                      ? ` · ${(a.options || []).length} options` : ""}
                  </div>
                </div>
                <Plus className="w-3.5 h-3.5 text-tb-navy/60 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AttributeRow({ i, row, onPatch, onRemove }) {
  const [optInput, setOptInput] = useState("");
  const addOption = () => {
    const v = optInput.trim();
    if (!v) return;
    if ((row.options || []).some((o) => o.toLowerCase() === v.toLowerCase())) {
      setOptInput("");
      return;
    }
    onPatch({ options: [...(row.options || []), v] });
    setOptInput("");
  };
  const removeOption = (idx) => {
    onPatch({ options: (row.options || []).filter((_, j) => j !== idx) });
  };
  const keyPreview = (row.name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return (
    <div className="rounded-xl border border-tb-navy/10 p-3 space-y-2"
         data-testid={`atlas-presets-attribute-row-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <TagIcon className="w-3.5 h-3.5 text-tb-navy/50" />
        <input
          type="text"
          value={row.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="Attribute name (e.g. Star rating)"
          data-testid={`atlas-presets-attribute-name-${i}`}
          className="flex-1 min-w-[180px] rounded-lg border border-tb-navy/15 px-3 py-1.5 text-sm text-tb-navy outline-none focus:border-tb-navy"
        />
        <select
          value={row.answer_type}
          onChange={(e) => onPatch({ answer_type: e.target.value })}
          data-testid={`atlas-presets-attribute-type-${i}`}
          className="rounded-lg border border-tb-navy/15 px-3 py-1.5 text-sm text-tb-navy outline-none focus:border-tb-navy bg-white"
        >
          {ANSWER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <button type="button" onClick={onRemove}
                aria-label="Remove"
                data-testid={`atlas-presets-attribute-remove-${i}`}
                className="text-tb-navy/50 hover:text-red-500">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {keyPreview && (
        <div className="text-[10px] font-hand text-tb-navy/40">
          Key: <code className="text-tb-navy/70">{keyPreview}</code>
        </div>
      )}
      {row.answer_type === "list" && (
        <div className="pt-1 border-t border-tb-navy/5"
             data-testid={`atlas-presets-attribute-options-${i}`}>
          <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-1.5">
            Choices
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(row.options || []).map((o, idx) => (
              <span key={idx}
                    className="inline-flex items-center gap-1 rounded-full bg-tb-navy/10 text-tb-navy px-2 py-0.5 text-[11px]">
                {o}
                <button type="button" onClick={() => removeOption(idx)}
                        aria-label={`Remove ${o}`}
                        className="text-tb-navy/50 hover:text-red-500">
                  <CloseIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={optInput}
              onChange={(e) => setOptInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
              placeholder="Type an option, press Enter"
              data-testid={`atlas-presets-attribute-option-input-${i}`}
              className="flex-1 rounded-lg border border-tb-navy/15 px-3 py-1.5 text-sm text-tb-navy outline-none focus:border-tb-navy"
            />
            <button type="button" onClick={addOption}
                    data-testid={`atlas-presets-attribute-option-add-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-tb-navy/10 hover:bg-tb-navy/20 text-tb-navy px-3 py-1 text-xs font-hand">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Preview panel — computes projected fan-out from selected
// targets + descendants toggle. Uses a lightweight dry-run POST
// so the count is authoritative (includes idempotent skips).
// ─────────────────────────────────────────────────────────────
function PreviewPanel({ selected, subFolders, attributes, onApply, busy }) {
  const [dryRun, setDryRun] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);

  const subFolderCount = subFolders.filter((s) => (s.name || "").trim()).length;
  const attributeCount = attributes.filter((a) => (a.name || "").trim()).length;
  const canApply = selected.length > 0 && (subFolderCount + attributeCount) > 0;

  const runPreview = useCallback(async () => {
    if (!canApply) return;
    setLoadingPreview(true);
    setPreviewErr(null);
    try {
      const r = await api.post("/atlas/admin/presets/apply", {
        targets: selected.map((s) => (s.type === "category"
          ? { type: "category", category_id: s.category_id }
          : { type: "folder", slug: s.slug, include_descendants: !!s.include_descendants })),
        sub_folders: subFolders.filter((s) => (s.name || "").trim()).map((s) => ({ name: s.name.trim() })),
        attributes: attributes.filter((a) => (a.name || "").trim()).map((a) => ({
          name: a.name.trim(),
          answer_type: a.answer_type || "string",
          options: a.answer_type === "list" ? (a.options || []) : undefined,
          // Msg 604 · Phase B — carry library id when picked from
          // the library so target folders reference the same entry.
          attribute_id: a.attribute_id || undefined,
        })),
        dry_run: true,
      });
      setDryRun(r.data);
    } catch (e) {
      setPreviewErr(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed.");
      setDryRun(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [selected, subFolders, attributes, canApply]);

  return (
    <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 sticky top-4"
         data-testid="atlas-presets-preview">
      <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-2">
        Preview
      </div>
      <div className="text-sm text-tb-navy space-y-1">
        <div>
          Will add <b data-testid="atlas-presets-preview-subfolder-count">{subFolderCount}</b> sub-folder{subFolderCount === 1 ? "" : "s"}{" "}
          and <b data-testid="atlas-presets-preview-attribute-count">{attributeCount}</b> attribute{attributeCount === 1 ? "" : "s"}
        </div>
        <div className="text-tb-navy/70">
          across <b data-testid="atlas-presets-preview-target-count">{selected.length}</b> selected folder{selected.length === 1 ? "" : "s"}
          {selected.some((s) => s.include_descendants) && (
            <span> (+ descendants)</span>
          )}.
        </div>
      </div>

      <button
        type="button"
        onClick={runPreview}
        disabled={!canApply || loadingPreview}
        data-testid="atlas-presets-preview-refresh"
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-tb-navy/25 px-3 py-1.5 text-xs font-hand text-tb-navy hover:bg-tb-navy/5 disabled:opacity-40"
      >
        {loadingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {dryRun ? "Refresh preview" : "Compute preview"}
      </button>

      {previewErr && (
        <div className="mt-2 text-xs text-red-600 font-hand"
             data-testid="atlas-presets-preview-error">
          {previewErr}
        </div>
      )}

      {dryRun && (
        <div className="mt-3 space-y-1 text-xs text-tb-navy border-t border-tb-navy/10 pt-3"
             data-testid="atlas-presets-preview-dryrun">
          <div>
            <b>{dryRun.resolved_target_count}</b> folders resolved
            (fan-out applied).
          </div>
          <div>
            + <b>{dryRun.added_folders}</b> new sub-folders,{" "}
            <span className="text-tb-navy/60">{dryRun.skipped_folders} already exist</span>
          </div>
          <div>
            + <b>{dryRun.added_attributes}</b> new attributes,{" "}
            <span className="text-tb-navy/60">{dryRun.skipped_attributes} already exist</span>
          </div>
          <div className="mt-2 max-h-40 overflow-y-auto text-[11px] text-tb-navy/70 space-y-0.5">
            {(dryRun.per_target || []).slice(0, 30).map((t) => (
              <div key={t.slug} className="truncate"
                   data-testid={`atlas-presets-preview-target-${t.slug}`}>
                • {t.name} <span className="text-tb-navy/40">({t.slug})</span>
              </div>
            ))}
            {(dryRun.per_target || []).length > 30 && (
              <div className="text-tb-navy/40">
                … and {(dryRun.per_target || []).length - 30} more.
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onApply}
        disabled={!canApply || busy}
        data-testid="atlas-presets-apply"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Apply preset
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Main page.
// ─────────────────────────────────────────────────────────────
export default function AtlasPresetsPage() {
  const [sp, setSp] = useSearchParams();
  const legacyParam = sp.get("legacy");
  const [legacyName, setLegacyName] = useState(legacyParam || "");
  useEffect(() => { setLegacyName(legacyParam || ""); }, [legacyParam]);

  const [selected, setSelected] = useState([]);   // [{type, slug|category_id, name, include_descendants?}]
  const [subFolders, setSubFolders] = useState([]); // [{name}]
  const [attributes, setAttributes] = useState([]); // [{name, answer_type, options}]
  const [busy, setBusy] = useState(false);

  // (2026-08-04) Persisted named presets. When a preset is loaded into
  // the editor, `loadedPresetId` is set so Save can update instead of
  // creating a duplicate. `savedPresets` holds the list rendered in
  // the "Saved presets" section above the editor.
  const [savedPresets, setSavedPresets] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [presetName, setPresetName] = useState("");
  const [loadedPresetId, setLoadedPresetId] = useState(null);

  const refreshSaved = useCallback(async () => {
    setLoadingSaved(true);
    try {
      const { data } = await api.get("/atlas/admin/folder-presets");
      setSavedPresets(data?.presets || []);
    } catch (e) {
      // Non-blocking — the editor still works if the list fetch fails.
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  useEffect(() => { refreshSaved(); }, [refreshSaved]);

  const clearEditor = () => {
    setSelected([]);
    setSubFolders([]);
    setAttributes([]);
    setPresetName("");
    setLoadedPresetId(null);
  };

  const loadPreset = (p) => {
    // Reconstruct `selected` from stored targets. `type=category`
    // targets need the human-readable category name looked up in the
    // hydrated categories cache; we fall back to the id when the
    // cache hasn't populated yet.
    const rebuilt = (p.targets || []).map((t) => {
      if ((t.type || "folder") === "category") {
        return { type: "category", category_id: t.category_id,
                 name: t.category_id || "category" };
      }
      return { type: "folder", slug: t.slug,
               include_descendants: !!t.include_descendants,
               name: t.slug };
    });
    setSelected(rebuilt);
    setSubFolders((p.sub_folders || []).map((s) => ({ name: s.name })));
    setAttributes((p.attributes || []).map((a) => ({
      name: a.name, answer_type: a.answer_type,
      options: a.options || [], attribute_id: a.attribute_id,
    })));
    setPresetName(p.name || "");
    setLoadedPresetId(p.id);
    toast.success(`Loaded preset '${p.name}' into the editor.`);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };

  const savePreset = async () => {
    const name = (presetName || "").trim();
    if (!name) { toast.error("Give the preset a name before saving."); return; }
    if (selected.length === 0) {
      toast.error("Pick at least one target before saving.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name,
        targets: selected.map((s) => (s.type === "category"
          ? { type: "category", category_id: s.category_id }
          : { type: "folder", slug: s.slug,
              include_descendants: !!s.include_descendants })),
        sub_folders: subFolders
          .filter((s) => (s.name || "").trim())
          .map((s) => ({ name: s.name.trim() })),
        attributes: attributes
          .filter((a) => (a.name || "").trim())
          .map((a) => ({
            name: a.name.trim(),
            answer_type: a.answer_type || "string",
            options: a.answer_type === "list" ? (a.options || []) : undefined,
            attribute_id: a.attribute_id || undefined,
          })),
      };
      if (loadedPresetId) {
        await api.patch(`/atlas/admin/folder-presets/${loadedPresetId}`, payload);
        toast.success(`Updated preset '${name}'.`);
      } else {
        await api.post("/atlas/admin/folder-presets", payload);
        toast.success(`Saved preset '${name}'. It will auto-apply to matching new folders.`);
      }
      await refreshSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const applySavedNow = async (p) => {
    if (!window.confirm(
      `Apply preset '${p.name}' to its ${(p.targets || []).length} target(s) now?\n\n` +
      `This is idempotent — anything already present will be skipped.`)) return;
    try {
      const { data } = await api.post(`/atlas/admin/folder-presets/${p.id}/apply`);
      toast.success(
        `Applied — +${data.added_folders} sub-folders, +${data.added_attributes} attributes ` +
        `(${(data.skipped_folders || 0) + (data.skipped_attributes || 0)} skipped).`);
      try { window.dispatchEvent(new Event("atlas:tree-invalidated")); } catch {}
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Apply failed.");
    }
  };

  const deleteSaved = async (p) => {
    if (!window.confirm(`Delete preset '${p.name}'?\n\nExisting folders that were previously affected will keep their sub-folders and attributes — this only removes the preset definition (so it stops auto-applying to new folders).`)) return;
    try {
      await api.delete(`/atlas/admin/folder-presets/${p.id}`);
      toast.success(`Deleted preset '${p.name}'.`);
      if (loadedPresetId === p.id) clearEditor();
      await refreshSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    }
  };

  // Msg 590 · Phase A follow-up (Issue 4) — accept a `prefill_attribute`
  // JSON blob from the URL so the CustomAttributeAdder's "Bulk apply →"
  // button can jump straight into Presets with the attribute already
  // populated. Consumed once on mount then stripped from the URL so
  // hard-refreshing doesn't re-prefill.
  useEffect(() => {
    const raw = sp.get("prefill_attribute");
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const one = {
        name: (parsed.name || "").toString().slice(0, 80),
        answer_type: parsed.answer_type || "string",
        options: Array.isArray(parsed.options) ? parsed.options : [],
      };
      if (one.name.trim()) setAttributes([one]);
    } catch { /* ignore malformed prefill */ }
    const next = new URLSearchParams(sp);
    next.delete("prefill_attribute");
    setSp(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissLegacy = () => {
    setLegacyName("");
    const next = new URLSearchParams(sp);
    next.delete("legacy");
    setSp(next, { replace: true });
  };

  const onApply = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/atlas/admin/presets/apply", {
        // Msg 598 · P0 fix — carry the discriminator + category_id
        // through on Apply the SAME way the Preview panel does.
        // Previously we mapped every selection to `{ slug, include_descendants }`,
        // which nuked the `type: "category"` targets (they have no
        // `.slug`) → backend saw only empty folder refs → 400
        // "No valid target topics." — surfaced to the user as the
        // "no topic was selected" error while a category was clearly
        // selected on the By-Category tab.
        targets: selected.map((s) => (s.type === "category"
          ? { type: "category", category_id: s.category_id }
          : { type: "folder", slug: s.slug, include_descendants: !!s.include_descendants })),
        sub_folders: subFolders.filter((s) => (s.name || "").trim()).map((s) => ({ name: s.name.trim() })),
        attributes: attributes.filter((a) => (a.name || "").trim()).map((a) => ({
          name: a.name.trim(),
          answer_type: a.answer_type || "string",
          options: a.answer_type === "list" ? (a.options || []) : undefined,
          // Msg 604 · Phase B — same library-id carry-through on Apply.
          attribute_id: a.attribute_id || undefined,
        })),
      });
      const d = r.data || {};
      toast.success(
        `Applied to ${d.resolved_target_count} folders — `
        + `+${d.added_folders} sub-folders, +${d.added_attributes} attributes `
        + `(${d.skipped_folders + d.skipped_attributes} already existed).`
      );
      // Reset the editor rows so admins don't accidentally re-apply.
      setSubFolders([]);
      setAttributes([]);
      // Nudge any mounted tree caches to refresh next mount. LeftRailTree
      // re-fetches on route change, so no in-place invalidation needed.
      try { window.dispatchEvent(new Event("atlas:tree-invalidated")); } catch {}
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preset apply failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="atlas-presets-page">
      <div className="mb-2">
        <Link to="/atlas/topics"
              data-testid="atlas-presets-back"
              className="inline-flex items-center gap-1 text-tb-navy/60 hover:text-tb-navy text-xs font-hand">
          <ArrowLeft className="w-3 h-3" /> Back to Topics
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold flex items-center gap-3"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            <Layers className="w-7 h-7 text-tb-gold-bright" />
            Folder Presets
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-2xl">
            Pick one or more folders (with an optional descendant fan-out) and
            apply a preset — sub-folders and attribute definitions land on
            every selected folder idempotently.
          </p>
        </div>
      </div>

      <LegacyBanner legacyName={legacyName} onDismiss={dismissLegacy} />

      {/* (2026-08-04) Saved presets — persisted named definitions that
          auto-apply to new matching folders. */}
      <section className="mb-6" data-testid="atlas-presets-section-saved">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-tb-navy text-lg font-semibold flex items-center gap-2"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            <Sparkles className="w-4 h-4 text-tb-gold-bright" />
            Saved presets
          </h2>
          <span className="text-[11px] font-hand text-tb-navy/50">
            {loadingSaved ? "loading…" : `${savedPresets.length} saved`}
          </span>
        </div>
        <p className="text-xs font-hand text-tb-navy/60 mb-3">
          Saved presets auto-apply to newly-created folders that match their targets —
          e.g., a preset targeting "Cruise Ship" category applies to every future cruise-ship folder.
          Click <b>Load</b> to edit an existing preset in the editor below.
        </p>
        {!loadingSaved && savedPresets.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-tb-gold-bright/60 bg-tb-gold-bright/5 p-5"
               data-testid="atlas-presets-saved-empty">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                <Sparkles className="w-6 h-6 text-tb-gold-bright" />
              </div>
              <div className="min-w-0">
                <div className="text-tb-navy text-base font-semibold"
                     style={{ fontFamily: '"Playfair Display", serif' }}>
                  No presets saved yet.
                </div>
                <p className="mt-1 text-xs font-hand text-tb-navy/70 leading-relaxed">
                  Create a preset by picking targets, sub-folders, and attributes below,
                  then click <b>Save preset</b>. Once saved, the preset will
                  auto-apply to any future folder that matches its targets — no
                  need to run it by hand.
                </p>
              </div>
            </div>
          </div>
        )}
        {savedPresets.length > 0 && (
          <div className="space-y-2">
            {savedPresets.map((p) => (
              <div key={p.id}
                   data-testid={`atlas-presets-saved-row-${p.id}`}
                   className={
                     "rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap " +
                     (loadedPresetId === p.id
                       ? "border-tb-gold-bright/60 bg-tb-gold-bright/5"
                       : "border-tb-navy/15 bg-white")
                   }>
                <div className="min-w-0 flex-1">
                  <div className="text-tb-navy text-sm font-semibold truncate">{p.name}</div>
                  <div className="mt-0.5 text-[11px] font-hand text-tb-navy/60">
                    {(p.targets || []).length} target{(p.targets || []).length === 1 ? "" : "s"} ·{" "}
                    {(p.sub_folders || []).length} sub-folder{(p.sub_folders || []).length === 1 ? "" : "s"} ·{" "}
                    {(p.attributes || []).length} attribute{(p.attributes || []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => loadPreset(p)}
                    data-testid={`atlas-presets-saved-load-${p.id}`}
                    className="h-8 px-3 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
                    Load
                  </button>
                  <button type="button" onClick={() => applySavedNow(p)}
                    data-testid={`atlas-presets-saved-apply-${p.id}`}
                    className="h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs hover:bg-tb-navy/90 inline-flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Apply now
                  </button>
                  <button type="button" onClick={() => deleteSaved(p)}
                    data-testid={`atlas-presets-saved-delete-${p.id}`}
                    aria-label={`Delete ${p.name}`}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-full text-red-700/70 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Save-preset input row — sits above the target picker so the
          admin picks a name up-front then fills in targets/subfolders/attrs. */}
      <section className="mb-4" data-testid="atlas-presets-section-save">
        <div className="rounded-lg border border-tb-navy/15 p-3 bg-white flex items-center gap-2 flex-wrap">
          <label className="text-[11px] font-hand uppercase tracking-widest text-tb-navy/50 shrink-0">
            Preset name
          </label>
          <input value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder={loadedPresetId ? "" : "e.g., Cruise Ship auto-scaffold"}
            data-testid="atlas-presets-name-input"
            className="flex-1 min-w-[220px] h-9 px-3 rounded-full border border-tb-navy/20 focus:border-tb-navy outline-none font-hand text-sm bg-white text-tb-navy" />
          <button type="button" onClick={savePreset} disabled={busy}
            data-testid="atlas-presets-save-btn"
            className="h-9 px-4 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {loadedPresetId ? "Update preset" : "Save preset"}
          </button>
          {loadedPresetId && (
            <button type="button" onClick={clearEditor}
              data-testid="atlas-presets-save-cancel"
              className="h-9 px-3 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs">
              Cancel edit
            </button>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          {/* Section 1 — targets */}
          <section data-testid="atlas-presets-section-targets">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-tb-navy text-lg font-semibold"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                1. Target folders
              </h2>
              <span className="text-[11px] font-hand text-tb-navy/50">
                {selected.length} selected
              </span>
            </div>
            <p className="text-xs font-hand text-tb-navy/60 mb-3">
              Search or expand the tree. Check any folder to add it as a target.
              Toggle <b>+ descendants</b> to fan-out to that folder's children.
            </p>
            <TargetPicker selected={selected} onChange={setSelected} />
          </section>

          {/* Section 2 — sub-folders */}
          <section data-testid="atlas-presets-section-subfolders">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-tb-navy text-lg font-semibold"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                2. Sub-folders to add
              </h2>
              <span className="text-[11px] font-hand text-tb-navy/50">
                {subFolders.filter((s) => (s.name || "").trim()).length}
              </span>
            </div>
            <p className="text-xs font-hand text-tb-navy/60 mb-3">
              Name-only. These become new child topics under each target folder.
              Skipped when a same-named child already exists.
            </p>
            <SubFoldersEditor rows={subFolders} onChange={setSubFolders} />
          </section>

          {/* Section 3 — attributes */}
          <section data-testid="atlas-presets-section-attributes">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-tb-navy text-lg font-semibold"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                3. Attributes to add
              </h2>
              <span className="text-[11px] font-hand text-tb-navy/50">
                {attributes.filter((a) => (a.name || "").trim()).length}
              </span>
            </div>
            <p className="text-xs font-hand text-tb-navy/60 mb-3">
              These appear as fillable rows on the target folder's focus view.
              Name is required; Answer Type controls the editor.
            </p>
            <AttributesEditor rows={attributes} onChange={setAttributes} />
          </section>
        </div>

        <div>
          <PreviewPanel
            selected={selected}
            subFolders={subFolders}
            attributes={attributes}
            onApply={onApply}
            busy={busy}
          />
          {selected.length === 0 && (
            <div className="mt-3 text-xs font-hand text-tb-navy/50 flex items-start gap-2"
                 data-testid="atlas-presets-empty-hint">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Pick at least one target folder to enable Apply.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
